import * as Logger from './logger.js'
import * as Utils from './utils.js'
import { HSComponent } from './component.js';
import { HSWorkflow } from './workflow.js';
import { UserSession } from './session.js';

export class HSGrid extends HSComponent {
    cardIdCounter = 1;
    gridIdCounter = 1;

    constructor() { 
        super();
    }

    // Recursively search the config tree for a grid with the given id.
    // Returns { config: gridConfig, parentConfig } or null.
    findGridWithParent(id, config = this.config) {
        for (const grid of config?.grids || []) {
            if (grid.id === id) return { config: grid, parentConfig: config };
            const found = this.findGridWithParent(id, grid);
            if (found) return found;
        }
        return null;
    }

    resolveTabGroup(tabGroup, id) {
        if (tabGroup) {
            return tabGroup;
        }
        if (!id) {
            return null;
        }

        const matches = this.findChildren({
            predicate: (child) => child instanceof HSComponent
                && child.config?.id == id
                && !!child.config?.tabGroup
        });

        if (matches.length < 1) {
            return null;
        }

        // if multiple cards share the same id across tab groups, prefer selected one, else first
        const selected = matches.find(item => item.selected);
        return (selected || matches[0]).config.tabGroup;
    }

    findActiveTab(tabGroup) {
        const items = this.findChildren({ predicate: child => child instanceof HSComponent && child.config.tabGroup == tabGroup });
        for (const item of items) {
            if (item.selected) {
                return item;
            }
        }
        return null;
    }

    findTabById(tabGroup, id) {
        const items = this.findChildren({ predicate: child => child instanceof HSComponent && child.config.tabGroup == tabGroup && child.config.id == id });
        if (items[0]) {
            return items[0];
        }
        return null;
    }

    pickDefaultTab(tabGroup) {
        const items = this.findChildren({ predicate: child => child instanceof HSComponent && child.config.tabGroup == tabGroup });
        for (const item of items) {
            if (item.id) {
                return item;
            }
        }
        return null;
    }

    unselectAllTabs(tabGroup) {
        const items = this.findChildren({ predicate: child => child instanceof HSComponent && child.config.tabGroup == tabGroup });
        items.forEach(item => {
            item.setSelected(false);
        });
    }

    selectTab(id, tabGroup) {
        const items = this.findChildren({ predicate: child => child instanceof HSComponent && child.config.tabGroup == tabGroup && child.config.id == id });
        if (items[0]) {
            items[0].setSelected(true);
        }
    }

    getRememberedTabId(tabGroup) {
        if (!tabGroup) {
            return null;
        }
        const selectedTabs = UserSession.get('selected_tabs') || {};
        return selectedTabs[tabGroup] || null;
    }

    rememberTabSelection(tabGroup, id) {
        if (!tabGroup || !id) {
            return;
        }
        const selectedTabs = { ...(UserSession.get('selected_tabs') || {}) };
        selectedTabs[tabGroup] = id;
        UserSession.set('selected_tabs', selectedTabs);
    }

    widgetLayoutComparitor(a, b) {
        // if y is not equal, return the smaller value
        if (Number.isInteger(a.y)) {
            if (Number.isInteger(b.y)) {
                if (a.y != b.y) {
                    return a.y - b.y;
                }
                // fall through to sort by x
            } else {
                return -1;  // a is before b
            }
        } else if (Number.isInteger(b.y)) {
            return 1;   // b is before a
        }
        if (Number.isInteger(a.x)) {
            if (Number.isInteger(b.x)) {
                return a.x - b.x;
            } else {
                return -1;  // a is before b
            }
        } else if (Number.isInteger(b.x)) {
            return 1;   // b is before a
        }
        return 0;
    }

    async runWorkflow(grid, workflow_id) {
        const config = await this.fetch('/conf/workflows/' + workflow_id);
        Logger.debug("running workflow: ", config);

        const id = `card-${this.cardIdCounter++}`;  
        const workflow = new HSWorkflow(id, config, this.drawCardContent.bind(this, grid));
        const widget = this.addCard(id, grid, workflow.getCardConfig());
        
        // store workflow reference on widget so build handler can access merged state
        widget._workflow = workflow;
        widget.setActionHandler('build', (opts) => {
            const workflowState = workflow.getMergedState();
            this.buildWidgetFromWorkflow(grid, widget, id, workflowState);
        });
        
        workflow.init(widget);
    }

    buildWidgetFromWorkflow(grid, widget, id, workflowState) {
        Logger.debug("building widget from workflow state: ", workflowState);
        
        // parse fields - stored as JSON string when set from onapply
        let fields = workflowState.fields || [];
        if (typeof fields === 'string') {
            try { fields = JSON.parse(fields); } catch(e) { fields = []; }
        }
        
        // parse y-axis fields similarly
        let chartYAxis = workflowState.chart_yAxis || [];
        if (typeof chartYAxis === 'string') {
            try { chartYAxis = JSON.parse(chartYAxis); } catch(e) { chartYAxis = []; }
        }

        // derive a readable title from the source path (e.g. "api/devices" → "Devices")
        const srcName = (workflowState.src || '').split('/').pop().replace(/[_-]/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase()) || 'Widget';

        const cardConfig = {
            type: workflowState.view_type === 'chart' ? 'hs-chart' : 'hs-table',
            title: srcName,
            src: workflowState.src,
            id: `widget-${Date.now()}`,
            // apply chosen grid dimensions (default medium: 6×3)
            w: parseInt(workflowState.widget_w, 10) || 6,
            h: parseInt(workflowState.widget_h, 10) || 3,
        };
        
        if (workflowState.view_type === 'table') {
            // build table config
            cardConfig.cols = fields.length > 0 ? fields : undefined;
            cardConfig.pageSize = parseInt(workflowState.table_pageSize, 10) || 25;
            cardConfig.exportTitle = srcName;
        } else if (workflowState.view_type === 'chart') {
            // build chart config using the field names chart.js expects
            const chartType = workflowState.chart_type || 'bar';
            const xAxis = workflowState.chart_xAxis || (fields[0]?.id ?? null);
            const yAxis = chartYAxis.length > 0 ? chartYAxis
                : fields.filter(f => f.id !== xAxis).map(f => f.id);

            cardConfig.chartType = chartType;
            cardConfig.xAxis = xAxis;
            // use array-yAxis (multiLineChart) when more than one y field
            if (yAxis.length > 1) {
                cardConfig.yAxis = yAxis;
                cardConfig.multiLineChart = true;
            } else {
                cardConfig.yAxis = yAxis[0] ?? null;
            }
            cardConfig.chartOptions = {
                xAxis: { type: 'category' },
                yAxis: { type: 'value' },
                tooltip: { trigger: 'axis' }
            };
        }
        
        Logger.debug("final card config: ", cardConfig);
        
        // close the workflow card and replace with the actual widget
        this.closeCard(grid, widget);
        
        // load the new card with the final config
        this.loadCard(grid, cardConfig);
    }

    loadCard(grid, config) {
        const id = `card-${this.cardIdCounter++}`;  
        const widget = this.addCard(id, grid, config);
        return this.drawCardContent(grid, widget, id, config);
    }

    redrawCard(id) {
        const child = this.shadowRoot.getElementById(id);
        if (child) {
            child.redraw();
        } else {
            Logger.warn("child not found to redraw: ", id);
        }
    }

    isConfigVisible(config, { defaultVisible = true } = {}) {
        if (!config) {
            return false;
        }
        if (config.displayIf) {
            try {
                if (!Utils.filter(Utils.compileFilters(config.displayIf, [ 'session' ]), [ UserSession ])) {
                    return false;
                }
            } catch (err) {
                Logger.warn('failed displayIf evaluation, hiding config: ', config, err);
                return false;
            }
        }
        if (Utils.key_exists(config, 'displayed')) {
            return !!config.displayed;
        }
        return defaultVisible;
    }

    isTabControlledGrid(parentConfig, gridConfig) {
        if (!parentConfig || !gridConfig) {
            return false;
        }
        if (parentConfig.tabbar) {
            return true;
        }
        const hasTabTrigger = (config) => {
            for (const card of config?.cards || []) {
                const onclick = card?.onclick;
                if (onclick?.action === 'showTabContent'
                    && (onclick.content || onclick.id) === gridConfig.id
                    && (!onclick.container || onclick.container === parentConfig.id)) {
                    return true;
                }
            }
            for (const grid of config?.grids || []) {
                if (hasTabTrigger(grid)) {
                    return true;
                }
            }
            return false;
        };
        return hasTabTrigger(parentConfig);
    }

    isGridVisible(parentConfig, gridConfig) {
        const tabControlled = this.isTabControlledGrid(parentConfig, gridConfig);
        const visible = this.isConfigVisible(gridConfig, {
            defaultVisible: !tabControlled,
        });
        Logger.debug('grid visibility decision: ', {
            parent_id: parentConfig?.id,
            grid_id: gridConfig?.id,
            displayed: Object.prototype.hasOwnProperty.call(gridConfig || {}, 'displayed') ? gridConfig.displayed : undefined,
            tabControlled,
            visible,
        });
        return visible;
    }

    matchesGridView(gridConfig, cardConfig) {
        if (gridConfig?.view && cardConfig?.view && gridConfig.view !== cardConfig.view) {
            return false;
        }
        // When a reportPicker is active and the card declares a reportView, only show
        // the card whose reportView matches the currently selected report.
        if (cardConfig?.reportView) {
            const activeReport = gridConfig?.activeReportView;
            if (activeReport && activeReport !== cardConfig.reportView) {
                return false;
            }
        }
        return true;
    }

    // Strip stale DOM element/widget/grid refs from a config object (and nested grids/cards).
    // Call this in destroy() so the next grid type receives a clean config.
    cleanConfigRefs(config) {
        if (!config) { return; }
        delete config.element;
        delete config.widget;
        delete config.grid;
        delete config.parentGrid;
        for (const g of config.grids || []) { this.cleanConfigRefs(g); }
        for (const c of config.cards || []) { this.cleanConfigRefs(c); }
    }

    bindGridHeader(container, config = {}, { eventTarget = container } = {}) {
        if (!container || !config?.title) {
            return;
        }
        const titleElement = container.querySelector('.grid-title');
        if (titleElement) {
            titleElement.textContent = config.title;
        }
        if (config.showDataCount) {
            const dataCount = container.querySelector('.data-count');
            if (dataCount) {
                this.onEvent('hs-data-count', (opts, ev) => {
                    // Ignore count updates from hidden report-view cards (e.g. engagement
                    // bundle card kept mounted but invisible while provider stats is active).
                    if (ev?.target?.config?.widget?.style?.display === 'none') return;
                    dataCount.textContent = opts.count;
                }, eventTarget);
            }
        }
    }

    removeSupplementalControls(container) {
        if (!container) {
            return;
        }
        const dateRangePicker = container.querySelector('hs-date-range-picker');
        if (dateRangePicker) {
            dateRangePicker.remove();
        }
        const searchContainer = container.querySelector('#search');
        if (searchContainer) {
            searchContainer.remove();
        }
        const viewPicker = container.querySelector('hs-view-picker');
        if (viewPicker) {
            viewPicker.remove();
        }
        const timeScopePicker = container.querySelector('hs-time-picker');
        if (timeScopePicker) {
            timeScopePicker.remove();
        }
        const snapshotPicker = container.querySelector('hs-snapshot-picker');
        if (snapshotPicker) {
            snapshotPicker.remove();
        }
        const statsColumnPicker = container.querySelector('hs-stats-column-picker');
        if (statsColumnPicker) {
            statsColumnPicker.remove();
        }
    }

    resolveSupplementalControls(config = {}, {
        activeView = 'tiles',
        hasStatsCard = false,
        isTabbar = false,
    } = {}) {
        const controls = config?.controls || {};
        const hasExplicit = key => Object.prototype.hasOwnProperty.call(controls, key);
        const hasTimeScopes = Array.isArray(config?.timeScopes) && config.timeScopes.length > 0;
        const statsViewActive = activeView === 'stats' && hasStatsCard;
        const timePicker = hasExplicit('timePicker')
            ? !!controls.timePicker
            : (hasTimeScopes && (!config?.viewPicker || statsViewActive));
        const snapshotPicker = hasExplicit('snapshotPicker') ? !!controls.snapshotPicker : statsViewActive;

        // Backward-compatible defaults from existing config fields,
        // while allowing explicit opt-in/opt-out via config.controls.
        const resolved = {
            search: hasExplicit('search') ? !!controls.search : !!config?.search,
            dateRangePicker: hasExplicit('dateRangePicker') ? !!controls.dateRangePicker : !!config?.dateRangePicker,
            viewPicker: hasExplicit('viewPicker') ? !!controls.viewPicker : !!config?.viewPicker,
            timePicker,
            snapshotPicker,
            statsColumnPicker: hasExplicit('statsColumnPicker') ? !!controls.statsColumnPicker : false,
            statsControlsRow: hasExplicit('statsControlsRow') ? !!controls.statsControlsRow : snapshotPicker,
        };

        if (isTabbar) {
            return {
                search: false,
                dateRangePicker: false,
                viewPicker: false,
                timePicker: false,
                snapshotPicker: false,
                statsColumnPicker: false,
                statsControlsRow: false,
            };
        }

        return resolved;
    }

    bindGridSearch(container, { enabled = false, onSearch } = {}) {
        const searchContainer = container?.querySelector('#search');
        if (!searchContainer) {
            return;
        }
        if (!enabled) {
            searchContainer.remove();
            return;
        }
        const searchInput = container.querySelector('#search-text');
        if (!searchInput) {
            Logger.warn('Search input not found in grid');
            return;
        }
        searchInput.addEventListener('keyup', ev => {
            if (ev.which === 27) {
                ev.target.value = '';
            }
            if (typeof onSearch === 'function') {
                onSearch((ev.target.value ?? '').trim());
            }
        }, { signal: this.abortController.signal });
    }

    bindDateRangePicker(container, { enabled = false } = {}) {
        const dateRangePicker = container?.querySelector('hs-date-range-picker');
        if (!dateRangePicker) {
            return;
        }
        if (!enabled) {
            dateRangePicker.remove();
            return;
        }
        this.onEvent('hs-date-changed', ev => {
            Logger.debug('DateChanged: ', ev.startDate, ev.endDate, ev.formattedValue);
        }, dateRangePicker);
    }

    bindViewPicker(container, { enabled = false, onViewChange, activeView = 'tiles' } = {}) {
        const viewPicker = container?.querySelector('hs-view-picker');
        if (!viewPicker) {
            return;
        }
        if (!enabled) {
            viewPicker.remove();
            return;
        }
        if (typeof onViewChange !== 'function') {
            return;
        }
        this.onEvent('hs-table-view', () => onViewChange('table'), viewPicker);
        this.onEvent('hs-tiles-view', () => onViewChange('tiles'), viewPicker);
        this.onEvent('hs-links-view', () => onViewChange('links'), viewPicker);
        this.onEvent('hs-stats-view', () => onViewChange('stats'), viewPicker);
        // Initialize picker selected state to match computed active view
        if (typeof viewPicker.setSelectedByView === 'function') {
            viewPicker.setSelectedByView(activeView);
        }
    }

    bindTimeScopes(container, { timeScopes = [], onTimeScope } = {}) {
        const timeScopePicker = container?.querySelector('hs-time-picker');
        if (!timeScopePicker) {
            return;
        }
        if (!Array.isArray(timeScopes) || timeScopes.length < 1) {
            timeScopePicker.remove();
            return;
        }
        timeScopePicker.init({ timeScopes });
        if (typeof onTimeScope === 'function') {
            this.onEvent('hs-day-view', () => onTimeScope('day'), timeScopePicker);
            this.onEvent('hs-week-view', () => onTimeScope('week'), timeScopePicker);
            this.onEvent('hs-month-view', () => onTimeScope('month'), timeScopePicker);
            this.onEvent('hs-quarter-view', () => onTimeScope('quarter'), timeScopePicker);
            this.onEvent('hs-all-view', () => onTimeScope('all'), timeScopePicker);
            if (!timeScopes.includes(UserSession.get('timeScope'))) {
                onTimeScope(timeScopes[timeScopes.length - 1]);
            }
        }
    }

    // Bind a report-picker <select> inside `container` using the `reports` array from config.
    // Calls `onReportChange(reportId)` when the user changes the selection.
    bindReportPicker(container, { reports = [], onReportChange } = {}) {
        const wrapper = container?.querySelector('.report-picker-wrapper');
        const select = container?.querySelector('.report-picker-select');
        if (!select) {
            return;
        }
        if (!Array.isArray(reports) || reports.length < 2) {
            // Nothing to pick — keep the wrapper hidden.
            if (wrapper) { wrapper.style.display = 'none'; }
            return;
        }
        select.innerHTML = '';
        for (const report of reports) {
            const option = document.createElement('option');
            option.value = report.id;
            option.textContent = report.label;
            select.appendChild(option);
        }
        // Restore previously-selected report from session if present.
        const saved = UserSession.get('activeReportView');
        if (saved && reports.some(r => r.id === saved)) {
            select.value = saved;
        }
        select.addEventListener('change', (ev) => {
            const reportId = ev.target.value;
            UserSession.set('activeReportView', reportId);
            if (typeof onReportChange === 'function') {
                onReportChange(reportId);
            }
        }, { signal: this.abortController.signal });
    }

    setTimeScope({ timeScope, subgrid = null } = {}) {
        Logger.debug('setting time scope: ', { timeScope, subgrid });
        UserSession.set('timeScope', timeScope);
        
        // Update snapshot picker if stats view is active
        // Scope picker search to the subgrid container to support encapsulated controls
        const container = subgrid ?? this.shadowRoot;
        const snapshotPicker = container?.querySelector('hs-snapshot-picker');
        let pickerHandledReload = false;
        const snapshotPickerVisible = !!snapshotPicker
            && snapshotPicker.style.display !== 'none'
            && snapshotPicker.style.visibility !== 'hidden';
        if (snapshotPickerVisible && typeof snapshotPicker.onTimeScopeChanged === 'function') {
            snapshotPicker.onTimeScopeChanged(timeScope);
            pickerHandledReload = true;
        }
        
        // Only manually reload components if no snapshot picker handled it,
        // to avoid duplicate reloadData calls
        if (!pickerHandledReload) {
            const components = this.findChildren({
                predicate: child => child instanceof HSComponent,
                shadowOnly: false,
                node: container
            });
            for (const component of components) {
                if (typeof component.reloadData === 'function') {
                    component.reloadData();
                }
            }
        }
    }

    bindSnapshotPicker(container, { enabled = false, analysisWindow = 'month' } = {}) {
        const snapshotPicker = container?.querySelector('hs-snapshot-picker');
        if (!snapshotPicker) {
            return;
        }
        if (!enabled) {
            snapshotPicker.style.display = 'none';
            return;
        }
        snapshotPicker.style.display = 'flex';

        // Register hs-snapshot-changed handler BEFORE init() so the initial
        // event (if one is emitted) is not missed
        if (!snapshotPicker.__snapshotReloadBound) {
            this.onEvent('hs-snapshot-changed', () => {
                const cards = this.findChildren({
                    node: container,
                    predicate: child => child instanceof HSComponent && typeof child.reloadData === 'function',
                    shadowOnly: false,
                });
                for (const component of cards) {
                    if (component !== snapshotPicker) {
                        // Skip components marked as excluded from scope-change triggers
                        if (component.hasAttribute && component.hasAttribute('data-exclude-scope-trigger')) {
                            continue;
                        }
                        component.reloadData();
                    }
                }
            }, snapshotPicker);
            snapshotPicker.__snapshotReloadBound = true;
        }

        snapshotPicker.init({ analysisWindow });
    }

    bindStatsColumnPresetPicker(container, { enabled = false } = {}) {
        const picker = container?.querySelector('hs-stats-column-picker');
        if (!picker) {
            return;
        }
        if (!enabled) {
            picker.style.display = 'none';
            return;
        }

        picker.style.display = 'flex';
        if (typeof picker.init === 'function') {
            picker.init();
        }

        if (!picker.__statsPresetBound) {
            this.onEvent('hs-stats-column-preset-changed', (opts) => {
                const components = this.findChildren({
                    node: container,
                    predicate: child => child instanceof HSComponent && typeof child.applyColumnPreset === 'function',
                    shadowOnly: false,
                });
                for (const component of components) {
                    component.applyColumnPreset(opts?.preset);
                }
            }, picker);
            picker.__statsPresetBound = true;
        }
    }

    setStatsControlsVisibility(container, visible = false, statsLeft = false) {
        const statsRow = container?.querySelector('.toolbar-row-stats');
        if (!statsRow) return;
        const gridHeader = statsRow.parentElement;
        if (!gridHeader) return;
        // Time picker sits in the stats row alongside the snapshot/report pickers on stats-card grids; otherwise it moves next to the search box in the primary row.
        const timePicker = container.querySelector('hs-time-picker');
        if (timePicker) {
            const desiredParent = statsLeft ? statsRow : container.querySelector('.toolbar-row-primary');
            if (desiredParent && timePicker.parentElement !== desiredParent) {
                desiredParent.appendChild(timePicker);
            }
        }
        if (visible) {
            gridHeader.classList.add(statsLeft ? 'stats-left' : 'stats-right');
            gridHeader.classList.remove(statsLeft ? 'stats-right' : 'stats-left');
        } else {
            gridHeader.classList.remove('stats-left', 'stats-right');
        }
        // Inject the comparison label only into grids that actually contain a stats card.
        // This avoids duplicate IDs across subgrids and keeps the label opt-in.
        let compLabel = statsRow.querySelector('.comparison-label');
        if (statsLeft && !compLabel) {
            compLabel = document.createElement('span');
            compLabel.className = 'comparison-label';
            compLabel.style.cssText = 'margin-left: 16px; font-weight: 500; color: #555;';
            statsRow.appendChild(compLabel);
        } else if (!statsLeft && compLabel) {
            compLabel.remove();
        }
    }
}
